import React, { useState, useEffect, useRef } from 'react';
import { useSignal } from '@preact/signals-react';
import { toast } from 'react-toastify';
import { TbPlus, TbEdit, TbTrash, TbFileText, TbUpload, TbDownload, TbEye } from 'react-icons/tb';
import { Auth } from 'aws-amplify';
import TextEdit from '../../../molecules/TextEdit';
import SelectTextField from '../../../molecules/SelectTextField';
import ConfirmationModal from '../../../templates/ConfirmationModal';
import FilePreview from '../../../atoms/FilePreview';
import appConfig from '../../../../config/app-config';

const { address: PERSON_API_URL, key: PERSON_API_KEY } = appConfig.apis.personApiEnv;
const BASE_URL = `${PERSON_API_URL}/person`;

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx', 'xls', 'xlsx'];

interface Document {
    id?: string;
    type: string;
    number: string;
    issuingAuthority?: string;
    expeditionDate?: string;
    expirationDate?: string;
    province?: string;
    fileUuid?: string;
    fileName?: string;
}

interface Props {
    personId: string;
    personType?: 'individual' | 'legal_person';
    documents?: Document[];
    onDocumentsChange?: (documents: Document[]) => void;
}

const getHeaders = async () => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return {
        'x-api-key': PERSON_API_KEY,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    };
};

const PersonFormDocuments: React.FC<Props> = ({
    personId,
    personType = 'individual',
    documents: initialDocuments = [],
    onDocumentsChange = () => { }
}) => {
    const isModalOpen = useSignal<boolean>(false);
    const isDeleteModalOpen = useSignal<boolean>(false);
    const isLoading = useSignal<boolean>(false);
    const documents = useSignal<Document[]>([]);

    const [editingDocument, setEditingDocument] = useState<Document | null>(null);
    const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewDoc, setPreviewDoc] = useState<{ url: string; fileName: string } | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [form, setForm] = useState<Document>({
        type: '', number: '', issuingAuthority: '', expeditionDate: '', expirationDate: '', province: ''
    });

    const documentTypes = personType === 'legal_person'
        ? [
            { value: 'CNPJ', label: 'CNPJ' },
            { value: 'INSCRICAO_ESTADUAL', label: 'Inscrição Estadual' },
            { value: 'CONTRATO_SOCIAL', label: 'Contrato Social ou Estatuto Social atualizado' },
            { value: 'ALVARA_FUNCIONAMENTO', label: 'Alvará de Funcionamento' },
            { value: 'CERTIDAO_NEGATIVA_DEBITOS', label: 'Certidão Negativa de Débitos' },
            { value: 'COMPROVANTE_ENDERECO', label: 'Comprovante de Endereço' },
            { value: 'ATA_CONSELHO_ADMINISTRACAO', label: 'Ata de eleição do Conselho de Administração' },
            { value: 'ATA_ELEICAO_DIRETORIA', label: 'Ata de eleição da diretoria' },
            { value: 'PROCURACAO_PUBLICA', label: 'Procuração Pública' },
            { value: 'INSTRUMENTO_PARTICULAR', label: 'Instrumento Particular' },
            { value: 'CARTAO_CNPJ', label: 'Cartão CNPJ (comprovante de inscrição e situação cadastral)' },
            { value: 'DOCUMENTO_IDENTIFICACAO', label: 'Documento de identificação (RG/CPF ou CNH)' },
            { value: 'CERTIDAO_CASAMENTO', label: 'Certidão de Casamento' },
            { value: 'COMPROVANTE_BANCARIO', label: 'Comprovante bancário' },
        ]
        : [
            { value: 'CPF', label: 'CPF' },
            { value: 'RG', label: 'RG' },
            { value: 'CNH', label: 'CNH' },
            { value: 'PASSPORT', label: 'Passaporte' },
            { value: 'RNE', label: 'RNE' },
            { value: 'CTPS', label: 'Carteira de Trabalho' },
            { value: 'TITULO_ELEITOR', label: 'Título de Eleitor' },
        ];

    const getDocumentTypeLabel = (type: string) => documentTypes.find(t => t.value === type)?.label || type;

    useEffect(() => {
        if (initialDocuments?.length > 0) documents.value = initialDocuments;
        if (personId) loadDocuments();
    }, [personId]);

    const openModal = (document?: Document) => {
        if (document) {
            setEditingDocument(document);
            setForm(document);
        } else {
            setEditingDocument(null);
            setForm({ type: documentTypes[0]?.value || '', number: '', issuingAuthority: '', expeditionDate: '', expirationDate: '', province: '' });
        }
        setSelectedFile(null);
        isModalOpen.value = true;
    };

    const closeModal = () => {
        isModalOpen.value = false;
        setEditingDocument(null);
        setSelectedFile(null);
        setForm({ type: documentTypes[0]?.value || '', number: '', issuingAuthority: '', expeditionDate: '', expirationDate: '', province: '' });
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const ext = file.name.split('.').pop()?.toLowerCase() || '';
        if (!ALLOWED_EXTENSIONS.includes(ext)) {
            toast.error(`Tipo de arquivo não permitido. Permitidos: ${ALLOWED_EXTENSIONS.join(', ')}`);
            setSelectedFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            toast.error('Arquivo excede o limite de 5MB');
            setSelectedFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }
        setSelectedFile(file);
    };

    const uploadFileForDocument = async (documentId: string) => {
        if (!selectedFile) return;
        const headers = await getHeaders();
        const reader = new FileReader();
        return new Promise<void>((resolve, reject) => {
            reader.onload = async () => {
                try {
                    const base64 = (reader.result as string).split(',')[1];
                    const res = await fetch(`${BASE_URL}/${personId}/documents/${documentId}/upload`, {
                        method: 'POST', headers,
                        body: JSON.stringify({ file: base64, file_name: selectedFile.name })
                    });
                    if (!res.ok) throw new Error('Upload failed');
                    resolve();
                } catch (err) { reject(err); }
            };
            reader.readAsDataURL(selectedFile);
        });
    };

    const handleSubmit = async () => {
        if (!form.type) { toast.error('Selecione o tipo de documento'); return; }

        isLoading.value = true;
        try {
            const headers = await getHeaders();
            const url = editingDocument ? `${BASE_URL}/${personId}/documents/${editingDocument.id}` : `${BASE_URL}/${personId}/documents`;
            const method = editingDocument ? 'PUT' : 'POST';

            const payload: any = {
                type: form.type, number: form.number,
                issuing_authority: form.issuingAuthority,
                expedition_date: form.expeditionDate,
                expiration_date: form.expirationDate,
                province: form.province,
            };

            const response = await fetch(url, { method, headers, body: JSON.stringify(payload) });
            if (!response.ok) { const err = await response.json(); toast.error(err.error || 'Erro ao salvar documento'); return; }

            const result = await response.json();
            const docId = result.document?.id || editingDocument?.id;

            if (selectedFile && docId) {
                try {
                    await uploadFileForDocument(docId);
                    toast.success(editingDocument ? 'Documento e arquivo atualizados!' : 'Documento e arquivo cadastrados!');
                } catch {
                    toast.warning('Documento salvo, mas houve erro no upload do arquivo');
                }
            } else {
                toast.success(editingDocument ? 'Documento atualizado!' : 'Documento cadastrado!');
            }

            await loadDocuments();
            closeModal();
        } catch (error) {
            toast.error('Erro ao salvar documento');
            console.error('Error saving document:', error);
        } finally {
            isLoading.value = false;
        }
    };

    const handleDelete = async () => {
        if (!documentToDelete) return;
        isLoading.value = true;
        try {
            const headers = await getHeaders();
            const response = await fetch(`${BASE_URL}/${personId}/documents/${documentToDelete}`, { method: 'DELETE', headers });
            if (response.ok) { toast.success('Documento excluído!'); await loadDocuments(); }
            else toast.error('Erro ao excluir documento');
        } catch { toast.error('Erro ao excluir documento'); }
        finally { isLoading.value = false; isDeleteModalOpen.value = false; setDocumentToDelete(null); }
    };

    const handlePreview = async (doc: Document) => {
        if (!doc.fileUuid || !doc.id) return;
        try {
            const headers = await getHeaders();
            const res = await fetch(`${BASE_URL}/${personId}/documents/${doc.id}/download-url`, { headers });
            if (res.ok) {
                const data = await res.json();
                setPreviewDoc({ url: data.downloadUrl, fileName: data.fileName || doc.fileName || 'file' });
            } else toast.error('Erro ao obter URL do arquivo');
        } catch { toast.error('Erro ao obter URL do arquivo'); }
    };

    const handleDownload = async (doc: Document) => {
        if (!doc.fileUuid || !doc.id) return;
        try {
            const headers = await getHeaders();
            const res = await fetch(`${BASE_URL}/${personId}/documents/${doc.id}/download-url`, { headers });
            if (res.ok) {
                const data = await res.json();
                window.open(data.downloadUrl, '_blank');
            } else toast.error('Erro ao obter URL do arquivo');
        } catch { toast.error('Erro ao obter URL do arquivo'); }
    };

    const loadDocuments = async () => {
        if (!personId) return;
        isLoading.value = true;
        try {
            const headers = await getHeaders();
            const response = await fetch(`${BASE_URL}/${personId}/documents`, { headers });
            if (response.ok) {
                const data = await response.json();
                documents.value = data.documents || data || [];
            }
        } catch (error) { console.error('Error loading documents:', error); documents.value = []; }
        finally { isLoading.value = false; }
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        try { return new Date(dateString).toLocaleDateString('pt-BR'); } catch { return dateString; }
    };

    return (
        <div className="p-4">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                    <TbFileText className="text-blue-600" /> Documentos
                </h3>
                <button id={`btn-add-document-${personType}`} onClick={() => openModal()} className="bg-blue-600 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-blue-700">
                    <TbPlus size={16} /> Novo Documento
                </button>
            </div>

            <div className="bg-white rounded-lg shadow">
                {isLoading.value ? (
                    <div className="p-8 text-center text-gray-500">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                        <p className="text-sm">Carregando documentos...</p>
                    </div>
                ) : documents.value.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        <TbFileText size={48} className="mx-auto mb-4 text-gray-300" />
                        <p>Nenhum documento cadastrado</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Tipo</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Número</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Órgão Emissor</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Expedição</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Arquivo</th>
                                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {documents.value.map((document, index) => (
                                    <tr key={document.id || index} className="hover:bg-gray-50">
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <TbFileText className="text-blue-600" />
                                                <span className="font-medium">{getDocumentTypeLabel(document.type)}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 font-mono text-sm">{document.number}</td>
                                        <td className="px-4 py-3 text-sm">{document.issuingAuthority || '-'}</td>
                                        <td className="px-4 py-3 text-sm">{formatDate(document.expeditionDate || '')}</td>
                                        <td className="px-4 py-3 text-sm">
                                            {document.fileUuid ? (
                                                <div className="flex items-center gap-1">
                                                    <button onClick={() => handlePreview(document)} className="text-blue-600 hover:text-blue-800" title="Visualizar">
                                                        <TbEye size={16} />
                                                    </button>
                                                    <button onClick={() => handleDownload(document)} className="text-green-600 hover:text-green-800" title="Download">
                                                        <TbDownload size={16} />
                                                    </button>
                                                    <span className="text-xs text-gray-400 truncate max-w-[120px]">{document.fileName}</span>
                                                </div>
                                            ) : (
                                                <span className="text-gray-400 text-xs">Sem arquivo</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <div className="flex justify-center gap-2">
                                                <button id={`btn-edit-document-${personType}-${index}`} onClick={() => openModal(document)} className="text-blue-600 hover:text-blue-800" title="Editar">
                                                    <TbEdit size={16} />
                                                </button>
                                                <button id={`btn-remove-document-${personType}-${index}`} onClick={() => { setDocumentToDelete(document.id!); isDeleteModalOpen.value = true; }} className="text-red-600 hover:text-red-800" title="Excluir">
                                                    <TbTrash size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal de Cadastro/Edição */}
            {isModalOpen.value && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <h3 className="text-lg font-semibold mb-4">
                            {editingDocument ? 'Editar Documento' : 'Novo Documento'}
                        </h3>
                        <div className="space-y-4">
                            <SelectTextField id={`document-type-${personType}`} label="Tipo de Documento *" value={form.type} onChange={(e) => setForm(prev => ({ ...prev, type: e.target.value }))} items={documentTypes} />
                            <TextEdit id={`document-number-${personType}`} label="Número do Documento" value={form.number} onChange={(e) => setForm(prev => ({ ...prev, number: e.target.value }))} placeholder="Digite o número do documento" />
                            <TextEdit id={`document-issuing-authority-${personType}`} label="Órgão Emissor" value={form.issuingAuthority || ''} onChange={(e) => setForm(prev => ({ ...prev, issuingAuthority: e.target.value }))} placeholder="Ex: SSP/SP, DETRAN/SP" />
                            <div className="grid grid-cols-2 gap-4">
                                <TextEdit id={`document-expedition-date-${personType}`} label="Data de Expedição" type="date" value={form.expeditionDate || ''} onChange={(e) => setForm(prev => ({ ...prev, expeditionDate: e.target.value }))} />
                                <TextEdit id={`document-expiration-date-${personType}`} label="Data de Validade" type="date" value={form.expirationDate || ''} onChange={(e) => setForm(prev => ({ ...prev, expirationDate: e.target.value }))} />
                            </div>
                            <TextEdit id={`document-province-${personType}`} label="UF" value={form.province || ''} onChange={(e) => setForm(prev => ({ ...prev, province: e.target.value }))} placeholder="Ex: SP, RJ, MG" maxLength={2} />

                            {/* Upload de arquivo */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Arquivo (PDF, imagem ou Office — máx. 5MB)</label>
                                {editingDocument?.fileUuid && !selectedFile && (
                                    <div className="flex items-center gap-2 mb-2 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-700">
                                        <TbFileText size={16} />
                                        <span className="truncate">{editingDocument.fileName}</span>
                                        <span className="text-xs text-green-500">(arquivo atual)</span>
                                    </div>
                                )}
                                <div
                                    className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                                    onClick={() => fileInputRef.current?.click()}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => { e.preventDefault(); const file = e.dataTransfer.files[0]; if (file) { const input = fileInputRef.current; if (input) { const dt = new DataTransfer(); dt.items.add(file); input.files = dt.files; handleFileSelect({ target: input } as any); } } }}
                                >
                                    <input ref={fileInputRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" onChange={handleFileSelect} />
                                    {selectedFile ? (
                                        <div className="flex items-center justify-center gap-2 text-blue-600">
                                            <TbUpload size={20} />
                                            <span className="font-medium">{selectedFile.name}</span>
                                            <span className="text-xs text-gray-400">({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                                        </div>
                                    ) : (
                                        <div className="text-gray-500">
                                            <TbUpload size={24} className="mx-auto mb-1" />
                                            <p className="text-sm">Clique ou arraste um arquivo aqui</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 mt-6">
                            <button id={`btn-cancel-document-${personType}`} onClick={closeModal} className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50">Cancelar</button>
                            <button id={`btn-save-document-${personType}`} onClick={handleSubmit} disabled={isLoading.value} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                                {isLoading.value ? 'Salvando...' : 'Salvar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Confirmação de Exclusão */}
            <ConfirmationModal
                isOpen={isDeleteModalOpen.value}
                onCancel={() => { isDeleteModalOpen.value = false; setDocumentToDelete(null); }}
                onConfirm={handleDelete}
                title="Excluir Documento"
                id={documentToDelete || ''}
                text="Tem certeza que deseja excluir este documento? Esta ação não pode ser desfeita."
            />

            {/* Modal de Pré-visualização */}
            {previewDoc && (
                <FilePreview url={previewDoc.url} fileName={previewDoc.fileName} onClose={() => setPreviewDoc(null)} />
            )}
        </div>
    );
};

export default PersonFormDocuments;
